import { DeepLink } from "@/components/common/deep-link/deep-link-encoding";
import { generateFullViewerUrl } from "@/utils/redirects";
import { addWebEditorRedirectToUrl } from "@/utils/web-editor-redirect";
import { GUID } from "@faro-lotv/ielement-types";

/** Query parameter naming the project, e.g. /app?m=editor&p=<projectId>&s=<sheet>&d=<code> */
const PROJECT_PARAM = "p";

/** Hash pairing a mode with the project, e.g. /app#editor/<projectId> */
const HASH_LINK = /^#[^/]+\/([^/?]+)/;

/** The viewer routes projects by GUID, so anything else cannot name one */
const PROJECT_ID_PATTERN = /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;

/**
 * What a WebEditor url says about the view to open.
 *
 * Only the project is read so far; the remaining parameters are supported by filling in `view`,
 * which the viewer url builder already knows how to encode.
 */
type WebEditorLink = {
  /** Project the WebEditor url points at */
  projectId: GUID;

  /** View to restore within that project */
  view?: DeepLink;
};

/** Reads one WebEditor url shape, returning nothing for a url that is not of that shape */
type WebEditorLinkReader = (url: URL) => WebEditorLink | undefined;

/**
 * @returns what the query parameters of a WebEditor url say
 * @param url the viewer was opened with
 */
function readSearchParamsLink({
  searchParams,
}: URL): WebEditorLink | undefined {
  const projectId = searchParams.get(PROJECT_PARAM);

  return projectId ? { projectId } : undefined;
}

/**
 * @returns what the hash of a WebEditor url says
 * @param url the viewer was opened with
 */
function readHashLink({ hash }: URL): WebEditorLink | undefined {
  const projectId = HASH_LINK.exec(hash)?.at(1);

  return projectId ? { projectId } : undefined;
}

/** The known WebEditor url shapes, tried in order */
const LINK_READERS: WebEditorLinkReader[] = [
  readSearchParamsLink,
  readHashLink,
];

/**
 * @returns what the first matching url shape reads, or undefined if none matches
 * @param url the viewer was opened with
 */
function readWebEditorLink(url: URL): WebEditorLink | undefined {
  for (const readLink of LINK_READERS) {
    const link = readLink(url);
    if (link) return link;
  }
}

/**
 * Translates a WebEditor url into the viewer url it leads to, marked as a redirect.
 *
 * @returns the viewer url, or undefined if the WebEditor url names no project
 * @param url the viewer was opened with
 */
export function parseWebEditorUrl(url: URL): URL | undefined {
  const link = readWebEditorLink(url);

  if (!link || !PROJECT_ID_PATTERN.test(link.projectId)) return;

  return addWebEditorRedirectToUrl(
    generateFullViewerUrl(link.projectId, link.view),
  );
}
